"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Cookies from "js-cookie";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Spinner } from "src/components/animation/Transition";
import {
  authFieldErrorClasses,
  authFieldLabelClasses,
  authFormAlertClasses,
  authPrimaryButtonClasses,
  authTextLinkClasses,
  getAuthInputClasses,
} from "src/app/auth/authFormStyles";
import { Button } from "src/components/ui/Button";
import {
  CLIENT_CSRF_COOKIE_NAME,
  enterAuthenticatedApp,
  getClientCookieOptions,
  retireLegacyCsrfCookie,
} from "src/lib/auth-client";
import { setDatadogUser } from "src/lib/monitoring/datadogUser";
import { zodResolverCompat } from "src/lib/validation/zodResolverCompat";
import { getAuthenticatedLandingPath } from "src/lib/auth-routing";

const validationSchema = z.object({
  email: z.string().min(1, "Email is required").email("Enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

type FormValues = z.infer<typeof validationSchema>;

const loginErrorMessage = "We couldn't sign you in. Check your email and password and try again.";

function getLoginErrorMessage(payload: unknown) {
  if (!payload || typeof payload !== "object") {
    return loginErrorMessage;
  }

  if ("detail" in payload && typeof payload.detail === "string" && payload.detail.length > 0) {
    return payload.detail;
  }

  if (
    "non_field_errors" in payload &&
    Array.isArray(payload.non_field_errors) &&
    typeof payload.non_field_errors[0] === "string"
  ) {
    return payload.non_field_errors[0];
  }

  return loginErrorMessage;
}

export default function LoginForm({
  returnUrl,
  forgotPasswordHref,
}: Readonly<{ returnUrl: string; forgotPasswordHref: string }>) {
  const [showPassword, setShowPassword] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loginSucceeded, setLoginSucceeded] = useState(false);

  // get functions to build form with useForm() hook
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolverCompat(validationSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const { errors, isSubmitting } = formState;
  const isBusy = isSubmitting || loginSucceeded;

  // The latch is never released on success because the page is about to be
  // replaced by a document load. The one way back to this same page is the
  // back/forward cache, which would otherwise restore it still "Signing in...".
  useEffect(() => {
    function releaseLatchOnRestore(event: PageTransitionEvent) {
      if (event.persisted) {
        setLoginSucceeded(false);
      }
    }

    window.addEventListener("pageshow", releaseLatchOnRestore);
    return () => window.removeEventListener("pageshow", releaseLatchOnRestore);
  }, []);
  const emailField = register("email", {
    onChange: () => {
      if (submitError) {
        setSubmitError(null);
      }
    },
  });
  const passwordField = register("password", {
    onChange: () => {
      if (submitError) {
        setSubmitError(null);
      }
    },
  });
  const emailErrorId = errors.email ? "login-email-error" : undefined;
  const passwordErrorId = errors.password ? "login-password-error" : undefined;
  const submitErrorId = submitError ? "login-submit-error" : undefined;

  async function onSubmit({ email, password }: FormValues) {
    setSubmitError(null);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/v1/login/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: email, password }),
        referrerPolicy: "origin-when-cross-origin",
        mode: "cors",
        credentials: "include",
      });

      const payload = (await response.json().catch(() => null)) as {
        token?: string;
        csrf_token?: string;
        user?: { id: string; email: string; name: string };
        detail?: string;
        non_field_errors?: string[];
      } | null;

      if (!response.ok || !payload?.token || !payload.user) {
        setSubmitError(getLoginErrorMessage(payload));
        return;
      }

      // Set client-domain cookies for web-next middleware and CSRF header reads.
      // On production (.caltana.com), the backend also sets cookies via Set-Cookie headers.
      // On localhost over http, we need to use 'lax' sameSite and no secure flag
      const clientCookieOptions = getClientCookieOptions();
      Cookies.set("auth_token", payload.token, clientCookieOptions);
      // A browser that signed in against an older build still carries a
      // host-only `csrftoken`, which would keep shadowing the API's cookie even
      // though we no longer write one.
      retireLegacyCsrfCookie();

      if (payload.csrf_token) {
        // Stored under our own name, never as `csrftoken`: a same-named cookie on
        // the app host shadows the API's own `csrftoken` in `document.cookie`, and
        // sending the shadow made logout fail with 403 CSRF Failed. See
        // CLIENT_CSRF_COOKIE_NAME.
        Cookies.set(CLIENT_CSRF_COOKIE_NAME, payload.csrf_token, clientCookieOptions);
      }

      setDatadogUser(payload.user);

      setLoginSucceeded(true);
      // A document load, not router.push(): see enterAuthenticatedApp().
      enterAuthenticatedApp(getAuthenticatedLandingPath(returnUrl));
    } catch {
      setLoginSucceeded(false);
      setSubmitError(loginErrorMessage);
    }
  }

  function onInvalid() {
    setSubmitError(null);
  }

  return (
    <form
      className="space-y-5"
      method="post"
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      noValidate
      aria-busy={isBusy}
      aria-describedby={submitErrorId}
    >
      <div className="space-y-2">
        <label htmlFor="email" className={authFieldLabelClasses}>
          Email
        </label>
        <input
          id="email"
          type="email"
          {...emailField}
          autoComplete="email"
          inputMode="email"
          required
          placeholder="you@example.com"
          aria-invalid={Boolean(errors.email)}
          aria-describedby={emailErrorId}
          aria-errormessage={emailErrorId}
          className={getAuthInputClasses({
            hasError: Boolean(errors.email),
            className: "pr-16 font-medium",
          })}
        />
        {errors.email && (
          <p id={emailErrorId} className={authFieldErrorClasses}>
            {errors.email.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="password" className={authFieldLabelClasses}>
          Password
        </label>
        <div className="relative">
          <input
            id="password"
            type={showPassword ? "text" : "password"}
            {...passwordField}
            autoComplete="current-password"
            required
            placeholder="Enter your password"
            aria-invalid={Boolean(errors.password)}
            aria-describedby={passwordErrorId}
            aria-errormessage={passwordErrorId}
            className={getAuthInputClasses({
              hasError: Boolean(errors.password),
              className: "pr-20 sm:pr-24 font-medium",
            })}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-3 flex items-center rounded-md px-1 text-dark-60 transition-colors hover:text-dark-100 focus:outline-none focus:ring-2 focus:ring-purple-100/50 focus:ring-offset-1 focus:ring-offset-white dark:text-gray-400 dark:hover:text-gray-200 dark:focus:ring-offset-gray-950"
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-controls="password"
            aria-pressed={showPassword}
          >
            {showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
        {errors.password && (
          <p id={passwordErrorId} className={authFieldErrorClasses}>
            {errors.password.message}
          </p>
        )}
      </div>

      {submitError && (
        <div id={submitErrorId} role="alert" aria-live="assertive" className={authFormAlertClasses}>
          {submitError}
        </div>
      )}

      <div className="space-y-4 pt-2">
        <Button
          type="submit"
          disabled={isBusy}
          fullWidth
          size="lg"
          className={authPrimaryButtonClasses}
        >
          {isBusy && <Spinner className="h-4 w-4" />}
          {isBusy ? "Signing in..." : "Sign In"}
        </Button>

        <Link href={forgotPasswordHref} className={authTextLinkClasses}>
          Forgot your password?
        </Link>
      </div>
    </form>
  );
}
